import { themeNames } from '@fluentui/react-teams';
import React, { createContext, useContext, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { CustomFormAnswer, CustomFormTemplate } from './domain/CustomForm';
import { Lifecycle } from './lifecycle/utils/typings';
import { FileProps } from './shared/components/EditTicketAttachments/EditTicketAttachments';
import { FilterPropsItems } from './shared/components/Filter';
import { ActivityItemProps } from './shared/components/TicketConversation/TicketConversation.interfaces';
import { LooseObject } from './shared/components/TicketForm';
import { ICustomProperty } from './shared/interfaces/customproperties.interface';
import { UserImage } from './shared/interfaces/platformuser.interface';
import { ISupportGroup, ITeam } from './shared/interfaces/supportGroup.interface';
import { Comment, Ticket } from './shared/interfaces/ticket.interface';
import { TicketProps } from './shared/interfaces/ticketproperties.interface';
import { MyWorkListViewType } from './shared/utils/constants';
import { CustomFormState, defaultCustomFormStateValue } from './templates/CustomForms/CustomForm.Interface';

export interface FilterDropdownProps {
  selectedValues: FilterPropsItems[];
  isOpen: boolean;
  isQueryEmpty: boolean;
  queryValue: string;
  queryValueApplied: string;
  collapseList: any[];
}

export interface KBContext {
  isContextOnly: boolean;
  prompts: any[];
}

export interface KBData {
  answer: string;
  context?: KBContext;
  id: number;
  questions: string[];
  source: string;
  metadata?: any[];
  status: string;
  modifiedById: number;
  createdById: number;
  lastUpdatedDateTime: string;
  agentOnly: boolean;
  enableOffering: boolean;
  requestOffering?: string;
  requestDescription: string,
  order: number,
  serviceOfferingId?: number
  serviceOffering?: string;
  ScopedMembers?:any;
}
export interface SynonymData {
  alterations: string[];
}
export interface ChatStudioData {
  query: string;
  numberOfResponses: number;
  includeOpenAiResults: boolean;
  threshold: number;
  agentOnly: boolean;
  includeDraft: boolean;
}

export interface LoadingKB {
  message: string;
  isLoading: boolean;
}

export interface ToUpdateKB {
  id: number;
  answer?: string;
  question?: { index: number; content: string };
  removedQuestionsIndex?: number[];
  type: 'answer' | 'question' | 'remove_question';
}

export interface RequestProps {
  isToggleClosed: boolean;
}

export interface IRequestPageConfig {
  TenantId: string;
  TriageChannel: string;
  EnableMultiDepartmentPicker: boolean;
  MultiDepartmentPickerMessage: string;
  EnableServiceCatalog: boolean;
  RequestBehavior: string;
  CompanyLogoUrl: string | null;
  AgentsCanEditTheirOwnComments: boolean;
  AgentsCanDeleteTheirOwnComments: boolean;
}

export interface AppStateValue {
  theme: themeNames;
  isInTeams: boolean;
  tickets?: Ticket[];
  closedTickets?: Ticket[];
  platformusers?: PlatformUser[];
  boardFilter: number | undefined;
  activeFilter: number | undefined;
  listFilter: number | undefined;
  menu: number;
  ticketStatus?: TicketProps[];
  categories?: TicketProps[];
  ticketTypes?: TicketProps[];
  priority?: TicketProps[];
  source?: TicketProps[];
  supportgroups?: ISupportGroup[];
  teams?: ITeam[];
  defaultTeam?: ITeam;
  defaultSupportGroup?: ISupportGroup | null;
  comments?: Comment[];
  isLoading: boolean;
  isError: boolean;
  errorConfig?: any | undefined;
  listChartFilterDate: number | undefined;
  editTicketId?: number | null;
  usersInRole?: any[];
  userImages?: UserImage;
  isSidenavCollapsed: boolean;
  isSidenavChanged: boolean;
  isSettingsSidenavCollapsed?: boolean;
  currentUserEmail?: string;
  currentUserId?: number;
  filterDropdown?: FilterDropdownProps;
  filterDropdownCount?: number;
  knowledgeBase: KBData[];
  deletedKB: number[];
  editedKB: ToUpdateKB[];
  loadingKB: LoadingKB;
  lastComments?: LooseObject[];
  userRoles: {
    roles: string[];
    isLoading: boolean;
    isError: boolean;
    isUnauthorized: boolean;
    isAuthorizedAgent: boolean;
  };
  isReload?: boolean;
  request: RequestProps;
  isLoadingSla: boolean;
  tableTicketsList?: Ticket[];
  tableCurrentPage?: number;
  tableTicketTotalCount?: number;
  activeTickets?: Ticket[];
  myWorkTickets?: Ticket[];
  deflectedTickets?: Ticket[];
  unassignedTickets?: Ticket[];
  isAddingTicket: boolean;
  ticketsSla: TicketSla[];
  filtersApplyCount: number;
  disableApplyFilterBtn: boolean;
  isFilterApplied: boolean;
  ticketApprovals?: any[];
  completedTicketApprovals?: any[];
  lifecycleApprovals?: any[];
  taskStatus?: TaskStatus[];
  ticketTask?: TicketTask[];
  hasActiveTasks?: boolean;
  hasPendingApprovals?: boolean;
  lifecycles?: Lifecycle[];
  tags?: any[];
  lifecyclePhaseMenu?: number | undefined;
  lifecyclePhaseMenuToken?: number;
  lifecycleMenuOpen?: boolean;
  me?: any;
  isRouteFromMyWork: boolean;
  selectedTaskOrApproval?: {
    type: MyWorkListViewType.Approvals | MyWorkListViewType.Tasks;
    id: number;
  };
  isLoadingTemplates?: boolean;
  reloadBoardOnAddTicket?: boolean;
  uploadingState?: UploadingFilesProps;
  teamsConfig?: IRequestPageConfig;
  lifecycleStatus?: CommonEnum[];
  resolutionCategory?: TicketProps[];
  resolution?:ResolutionDetail;
  forms?: any[],
  customForms: any[],
  currentCustomForm: any,
  previousCustomForm: any,
  showCustomFormNextButton: boolean,
  showCustomFormBackButton: boolean,
  showCustomFormSaveButton: boolean,
  showCustomFormStartOverButton: boolean,
  isCustomFormSummaryPage: boolean,
  answerCustomFormNow : boolean,
  currentCustomFormIndex: number,
  previousFormIndexes: number[],
  openSendFormDialogFrom: string,
  customFormSendToUserId: number,
  isTVA: boolean,
  currentCustomFormId: number,
  customFormState?: CustomFormState
  cardTemplateJSON?: CustomFormTemplate;
  cardAnswerJSON?: CustomFormAnswer;
  activities: ActivityItemProps[];
  isSendFormLoading: boolean;
  customProperties: ICustomProperty[];
  draftComment?: { ticketId: number; body: any } | null;
}

export interface UploadingFilesProps {
  isUploading: boolean;
  hasStashed: boolean;
  acceptedFiles: File[];
  rejectedFiles: FileRejection[];
  filesOnDrive: FileProps[];
  uploadedFiles: FileProps[];
}

export interface AppStateKBValue {
  knowledgeBase: KBData[];
  synonyms: SynonymData[];
  deletedKB: number[];
  editedKB: ToUpdateKB[];
  loadingKB: LoadingKB;
  users: PlatformUser[];
}

// A direct URL load renders the side nav (and the ticket route guard) before meAuthentication()'s
// api.me() round trip returns, so both would otherwise start from an empty roles array on every
// full page load and the nav would stay blank until that call resolves. Seeding from the last
// confirmed roles lets them render correctly immediately; meAuthentication overwrites both the
// cache and this state once the fresh call resolves.
const CACHED_PLATFORM_ROLES_KEY = 'cachedPlatformRoles';

export const getCachedPlatformRoles = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(CACHED_PLATFORM_ROLES_KEY)) ?? [];
  } catch {
    return [];
  }
};

export const setCachedPlatformRoles = (roles: string[]) => {
  localStorage.setItem(CACHED_PLATFORM_ROLES_KEY, JSON.stringify(roles ?? []));
};

export const defaultStateValue: AppStateValue = {
  theme: themeNames.Default,
  isInTeams: false,
  filterDropdown: {
    selectedValues: [],
    isOpen: false,
    isQueryEmpty: true,
    queryValue: '',
    queryValueApplied: '',
    collapseList: [],
  },
  tickets: [],
  closedTickets: [],
  platformusers: [],
  boardFilter: 0,
  activeFilter: 0,
  listFilter: undefined,
  menu: 0,
  ticketStatus: [],
  categories: [],
  ticketTypes: [],
  priority: [],
  supportgroups: [],
  teams: [],
  comments: [],
  isLoading: true,
  isError: false,
  errorConfig: {},
  listChartFilterDate: 0,
  editTicketId: null,
  usersInRole: [],
  isSidenavCollapsed: false,
  isSidenavChanged: false,
  isSettingsSidenavCollapsed: true,
  filterDropdownCount: 0,
  knowledgeBase: [],
  deletedKB: [],
  editedKB: [],
  loadingKB: {
    message: '',
    isLoading: false,
  },
  userRoles: {
    roles: getCachedPlatformRoles(),
    isLoading: true,
    isError: false,
    isUnauthorized: false,
    isAuthorizedAgent: false
  },
  isReload: true,
  request: {
    isToggleClosed: false,
  },
  isLoadingSla: false,
  tableTicketsList: [],
  tableCurrentPage: 1,
  tableTicketTotalCount: 0,
  activeTickets: [],
  myWorkTickets: [],
  deflectedTickets: [],
  unassignedTickets: [],
  isAddingTicket: false,
  ticketsSla: [],
  tags: [],
  filtersApplyCount: 0,
  disableApplyFilterBtn: false,
  isFilterApplied: false,
  lifecycleMenuOpen: false,
  isRouteFromMyWork: false,
  uploadingState: {
    isUploading: false,
    acceptedFiles: [],
    filesOnDrive: [],
    hasStashed: false,
    rejectedFiles: [],
    uploadedFiles: [],
  },
  teamsConfig: null,
  resolutionCategory: [],
  activities: [],
  forms: [],
  customForms: [],
  currentCustomForm: null,
  previousCustomForm: null,
  showCustomFormNextButton: false,
  showCustomFormBackButton: false,
  showCustomFormSaveButton: false,
  showCustomFormStartOverButton: false,
  isCustomFormSummaryPage: false,
  answerCustomFormNow: false,
  currentCustomFormIndex: 0,
  previousFormIndexes: [],
  openSendFormDialogFrom: 'applyTemplate',
  customFormSendToUserId: null,
  isTVA: true,
  currentCustomFormId: null,
  customFormState: defaultCustomFormStateValue,
  isSendFormLoading: false,
  customProperties: [],
  draftComment: null
};

export const AppStateContext = createContext(defaultStateValue);

export const AppSetStateContext = createContext<
  React.Dispatch<React.SetStateAction<AppStateValue>> | undefined
>(undefined);

export const useSetState = () => {
  const setState = useContext(AppSetStateContext);
  if (!setState) {
    throw new Error(
      'useSetState was called outside of the AppSetStateContext provider'
    );
  }
  return setState;
};

export const appState = () => {
  const state = useContext(AppStateContext);
  if (!state) {
    throw new Error(
      'useSetState was called outside of the AppStateContext provider'
    );
  }
  return state;
};

interface Props {
  children: any;
}

const AppStateProvider: React.FC<Props> = ({ children }) => {
  const [state, setState] = useState(defaultStateValue);
  return (
    <AppStateContext.Provider value={state}>
      <AppSetStateContext.Provider value={setState}>
        {children}
      </AppSetStateContext.Provider>
    </AppStateContext.Provider>
  );
};

export default AppStateProvider;
